// Signed direct-to-Cloudinary upload with progress, per specs/plan.md's
// "Cloudinary" section: the client POSTs a multipart form built from a
// server-issued signature straight to Cloudinary's upload_url. XHR (not
// fetch) is used because it's the only way to get upload progress events.
import type { ResourceType, SignResponse } from "./types"

export interface UploadedAsset {
	cloudinary_public_id: string
	resource_type: ResourceType
	format: string
	width: number
	height: number
	duration_seconds: number
	bytes: number
}

export interface UploadProgress {
	loaded: number
	total: number
}

export function uploadToCloudinary(
	file: File | Blob,
	sign: SignResponse,
	resourceType: ResourceType,
	onProgress?: (progress: UploadProgress) => void,
): Promise<UploadedAsset> {
	return new Promise((resolve, reject) => {
		// Fail before spending the upload: Cloudinary transforms video during
		// the upload request and refuses anything past its sync-processing
		// limit, so a too-big file can only ever end in an error.
		if (sign.max_bytes && file.size > sign.max_bytes) {
			const limit = Math.round(sign.max_bytes / 1_000_000)
			reject(
				new Error(
					`This video is ${Math.round(file.size / 1_000_000)} MB, over the ${limit} MB limit. Try a shorter clip.`,
				),
			)
			return
		}

		const form = new FormData()
		form.append("file", file)
		form.append("api_key", sign.api_key)
		form.append("timestamp", String(sign.timestamp))
		form.append("folder", sign.folder)
		form.append("signature", sign.signature)
		// Signed alongside the rest, so they have to be posted exactly as
		// issued or Cloudinary rejects the signature.
		if (sign.transformation) form.append("transformation", sign.transformation)
		if (sign.format) form.append("format", sign.format)
		if (sign.eager) form.append("eager", sign.eager)
		if (sign.eager_async) form.append("eager_async", sign.eager_async)

		const xhr = new XMLHttpRequest()
		xhr.open("POST", sign.upload_url)
		xhr.upload.onprogress = (event) => {
			if (onProgress && event.lengthComputable) {
				onProgress({ loaded: event.loaded, total: event.total })
			}
		}
		xhr.onload = () => {
			if (xhr.status < 200 || xhr.status >= 300) {
				// Cloudinary explains itself in the body — most usefully when a
				// video is too big to transform during upload. Show that rather
				// than a bare status code.
				reject(
					new Error(cloudinaryError(xhr) ?? `Upload failed (${xhr.status})`),
				)
				return
			}
			try {
				const data = JSON.parse(xhr.responseText)
				resolve({
					cloudinary_public_id: data.public_id,
					resource_type: (data.resource_type as ResourceType) ?? resourceType,
					format: data.format ?? "",
					width: data.width ?? 0,
					height: data.height ?? 0,
					duration_seconds: data.duration ?? 0,
					bytes: data.bytes ?? 0,
				})
			} catch {
				reject(new Error("Cloudinary returned an unexpected response"))
			}
		}
		xhr.onerror = () => reject(new Error("Network error while uploading"))
		xhr.send(form)
	})
}

/** Cloudinary's error message from a failed upload response, if it sent one. */
function cloudinaryError(xhr: XMLHttpRequest): string | null {
	try {
		const message = JSON.parse(xhr.responseText)?.error?.message
		return typeof message === "string" && message !== "" ? message : null
	} catch {
		return null
	}
}
